import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Sidebar from '../Sidebar/Sidebar';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendarCheck, faCheck, faTimes, faClock, faUserClock, faSave, faListAlt, faEnvelopeOpenText, faCircleCheck, faCircleXmark } from '@fortawesome/free-solid-svg-icons';
import { buildApiUrl } from '../../config/api.config';

const Attendance = ({ showAlert }) => {
  const [activeTab, setActiveTab] = useState('live'); // 'live' or 'leaves'
  const [staffList, setStaffList] = useState([]);
  const [attendanceData, setAttendanceData] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date().toLocaleDateString('en-CA'));
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState(false);
  const [selectedStaff, setSelectedStaff] = useState(null);
  const [newBalance, setNewBalance] = useState(0);
  const [showBalanceModal, setShowBalanceModal] = useState(false);

  useEffect(() => {
    fetchData();
  }, [selectedDate, activeTab]);

  const calculateDuration = (checkIn, checkOut) => {
    if (!checkIn) return null;
    const start = new Date(checkIn);
    const end = checkOut ? new Date(checkOut) : new Date();
    const diffMs = end - start;
    
    if (diffMs < 0) return "0m";
    
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    
    if (hours > 0) {
      return `${hours}h ${minutes}m`;
    }
    return `${minutes}m`;
  };

  const fetchData = async () => {
    try {
      setLoading(true);
      const restaurantId = localStorage.getItem('currentRestaurantId');
      const token = localStorage.getItem('token');

      // Fetch staff list
      const staffRes = await axios.post(buildApiUrl("staff/list"), 
        { restaurantId },
        { headers: { 'auth-token': token } }
      );
      setStaffList(staffRes.data);

      // Fetch attendance
      const date = new Date(selectedDate);
      const month = date.getMonth() + 1;
      const year = date.getFullYear();

      const attendanceRes = await axios.post(buildApiUrl("hr/attendance/monthly"),
        { restaurantId, month, year },
        { headers: { 'auth-token': token } }
      );
      
      setAttendanceData(attendanceRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleLeaveAction = async (attendanceId, status, leaveType = 'unpaid') => {
    try {
      setActionLoading(true);
      const token = localStorage.getItem('token');
      await axios.post(buildApiUrl("hr/attendance/leave/approve"),
        { attendanceId, status, leaveType },
        { headers: { 'auth-token': token } }
      );
      if (showAlert) showAlert(`Leave ${status}!`, "success");
      fetchData();
    } catch (error) {
      console.error("Error updating leave:", error);
      if (showAlert) showAlert(error.response?.data?.message || "Failed to update leave", "danger");
    } finally {
      setActionLoading(false);
    }
  };

  const updateBalance = async () => {
    try {
        setActionLoading(true);
        const token = localStorage.getItem('token');
        await axios.post(buildApiUrl("hr/attendance/leave/balance/update"),
            { staffId: selectedStaff._id, balance: newBalance },
            { headers: { 'auth-token': token } }
        );
        if (showAlert) showAlert("Balance updated!", "success");
        setShowBalanceModal(false);
        fetchData();
    } catch (error) {
        console.error("Error updating balance:", error);
    } finally {
        setActionLoading(false);
    }
  };

  // Filter attendance for the selected date
  const selectedDateAttendance = attendanceData.filter(a => 
    new Date(a.date).toLocaleDateString('en-CA') === selectedDate
  );

  // Filter leave requests (all pending leaves for the restaurant)
  const leaveRequests = attendanceData.filter(a => a.leaveStatus === 'pending');

  return (
    <div style={{ display: 'flex', height: '100vh', background: '#0a0a0c', color: 'white' }}>
      <Sidebar />
      <div style={{ flex: 1, padding: '2rem', overflowY: 'auto' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '2rem' }}>
          <div>
            <h1 style={{ fontSize: '2rem', fontWeight: '800', margin: 0 }}>
              <FontAwesomeIcon icon={faCalendarCheck} style={{ marginRight: '1rem', color: '#22c55e' }} />
              Payroll Attendance
            </h1>
            <p style={{ color: 'rgba(255,255,255,0.5)', margin: '0.5rem 0 0' }}>Manage staff check-ins and leave requests in one place.</p>
          </div>
          <input 
            type="date" 
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            style={{ padding: '0.8rem 1rem', background: '#1c1c1f', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white' }}
          />
        </div>

        {/* Tabs */}
        <div style={{ display: 'flex', gap: '2rem', marginBottom: '2rem', borderBottom: '1px solid rgba(255,255,255,0.05)' }}>
          <button 
            onClick={() => setActiveTab('live')}
            style={{ padding: '1rem 0', background: 'none', border: 'none', color: activeTab === 'live' ? '#22c55e' : 'rgba(255,255,255,0.5)', cursor: 'pointer', fontWeight: 'bold', borderBottom: activeTab === 'live' ? '2px solid #22c55e' : 'none', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <FontAwesomeIcon icon={faListAlt} /> Live Logs
          </button>
          <button 
            onClick={() => setActiveTab('leaves')}
            style={{ padding: '1rem 0', background: 'none', border: 'none', color: activeTab === 'leaves' ? '#f59e0b' : 'rgba(255,255,255,0.5)', cursor: 'pointer', fontWeight: 'bold', borderBottom: activeTab === 'leaves' ? '2px solid #f59e0b' : 'none', display: 'flex', alignItems: 'center', gap: '0.5rem' }}
          >
            <FontAwesomeIcon icon={faEnvelopeOpenText} /> 
            Leave Requests {leaveRequests.length > 0 && <span style={{ background: '#f59e0b', color: 'black', borderRadius: '50%', padding: '0.1rem 0.4rem', fontSize: '0.7rem' }}>{leaveRequests.length}</span>}
          </button>
        </div>

        {loading ? (
          <div style={{ textAlign: 'center', padding: '3rem', color: 'rgba(255,255,255,0.5)' }}>Loading...</div>
        ) : activeTab === 'live' ? (
          /* LIVE ATTENDANCE LOG */
          <div style={{ background: '#1c1c1f', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)', overflow: 'hidden' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', textAlign: 'left' }}>
              <thead style={{ background: 'rgba(255,255,255,0.02)', color: 'rgba(255,255,255,0.4)', fontSize: '0.8rem' }}>
                <tr>
                  <th style={{ padding: '1.2rem' }}>STAFF MEMBER</th>
                  <th style={{ padding: '1.2rem' }}>STATUS</th>
                  <th style={{ padding: '1.2rem' }}>CHECK IN</th>
                  <th style={{ padding: '1.2rem' }}>CHECK OUT</th>
                  <th style={{ padding: '1.2rem' }}>SHIFT TIME</th>
                </tr>
              </thead>
              <tbody>
                {staffList.map(staff => {
                  const record = selectedDateAttendance.find(a => (a.staffId._id || a.staffId) === staff._id);
                  const duration = calculateDuration(record?.checkIn, record?.checkOut);

                  return (
                    <tr key={staff._id} style={{ borderBottom: '1px solid rgba(255,255,255,0.03)' }}>
                      <td style={{ padding: '1.2rem' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '1rem' }}>
                          <div style={{ width: '40px', height: '40px', borderRadius: '8px', background: 'rgba(255,255,255,0.05)', display: 'flex', alignItems: 'center', justifyContent: 'center', fontWeight: 'bold', color: '#22c55e' }}>{staff.name.charAt(0)}</div>
                          <div>
                            <div style={{ fontWeight: '600' }}>{staff.name}</div>
                            <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'capitalize' }}>{staff.staffRole}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: '1.2rem' }}>
                        <span style={{ 
                          padding: '0.3rem 0.6rem', 
                          borderRadius: '4px', 
                          fontSize: '0.75rem', 
                          fontWeight: '700', 
                          textTransform: 'uppercase',
                          background: record ? (record.status === 'present' ? '#22c55e20' : record.status === 'absent' ? '#ef444420' : '#f59e0b20') : 'rgba(255,255,255,0.05)',
                          color: record ? (record.status === 'present' ? '#22c55e' : record.status === 'absent' ? '#ef4444' : '#f59e0b') : 'rgba(255,255,255,0.3)'
                        }}>
                          {record ? record.status : 'None'}
                        </span>
                      </td>
                      <td style={{ padding: '1.2rem', color: record?.checkIn ? 'white' : 'rgba(255,255,255,0.2)' }}>
                        {record?.checkIn ? new Date(record.checkIn).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                      </td>
                      <td style={{ padding: '1.2rem', color: record?.checkOut ? 'white' : 'rgba(255,255,255,0.2)' }}>
                        {record?.checkOut ? new Date(record.checkOut).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' }) : '--:--'}
                      </td>
                      <td style={{ padding: '1.2rem' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                            <span>{duration ? <span style={{ color: '#3b82f6', fontWeight: 'bold' }}>{duration}</span> : '--'}</span>
                            <button 
                                onClick={() => {
                                    setSelectedStaff(staff);
                                    setNewBalance(staff.leaveBalance || 0);
                                    setShowBalanceModal(true);
                                }}
                                style={{ background: 'rgba(255,255,255,0.05)', border: 'none', color: '#3b82f6', fontSize: '0.7rem', padding: '0.3rem 0.5rem', borderRadius: '4px', cursor: 'pointer' }}
                            >
                                <span style={{ color: 'rgba(255,255,255,0.4)' }}>Bal: </span>{staff.leaveBalance || 0}d
                            </button>
                        </div>
                      </td>
                    </tr>
                  )
                })}
              </tbody>
            </table>
          </div>
        ) : (
          /* LEAVE REQUESTS TAB */
          <div style={{ display: 'grid', gap: '1rem' }}>
            {leaveRequests.length === 0 ? (
              <div style={{ textAlign: 'center', padding: '5rem', color: 'rgba(255,255,255,0.3)', background: '#1c1c1f', borderRadius: '16px' }}>
                <FontAwesomeIcon icon={faEnvelopeOpenText} style={{ fontSize: '3rem', marginBottom: '1rem' }} />
                <h3>No pending leave requests</h3>
              </div>
            ) : (
                leaveRequests.map(leave => (
                  <div key={leave._id} style={{ background: '#1c1c1f', padding: '1.5rem', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.05)', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <div style={{ display: 'flex', alignItems: 'center', gap: '1.5rem' }}>
                      <div style={{ width: '50px', height: '50px', borderRadius: '12px', background: '#f59e0b20', color: '#f59e0b', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '1.2rem', fontWeight: 'bold' }}>
                        {leave.staffId?.name?.charAt(0)}
                      </div>
                      <div>
                        <div style={{ fontWeight: 'bold', fontSize: '1.1rem' }}>{leave.staffId?.name}</div>
                        <div style={{ fontSize: '0.8rem', color: 'rgba(255,255,255,0.5)', marginTop: '0.2rem' }}>Requested for: <span style={{ color: 'white' }}>{new Date(leave.date).toLocaleDateString()}</span></div>
                        <div style={{ fontSize: '0.85rem', background: 'rgba(255,255,255,0.03)', padding: '0.5rem 1rem', borderRadius: '8px', marginTop: '0.8rem', borderLeft: '3px solid #f59e0b' }}>
                          "{leave.leaveReason || 'No reason provided'}"
                        </div>
                      </div>
                    </div>
                    
                    <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
                      <div style={{ textAlign: 'right', marginRight: '1rem' }}>
                        <div style={{ fontSize: '0.7rem', color: 'rgba(255,255,255,0.4)', textTransform: 'uppercase', marginBottom: '0.3rem' }}>Approve as</div>
                        <div style={{ display: 'flex', gap: '0.5rem' }}>
                            <button 
                                onClick={() => handleLeaveAction(leave._id, 'approved', 'paid')}
                                disabled={actionLoading}
                                style={{ padding: '0.6rem 1rem', background: '#22c55e', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem' }}
                            >
                                Paid
                            </button>
                            <button 
                                onClick={() => handleLeaveAction(leave._id, 'approved', 'unpaid')}
                                disabled={actionLoading}
                                style={{ padding: '0.6rem 1rem', background: '#3b82f6', color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold', fontSize: '0.8rem' }}
                            >
                                Unpaid
                            </button>
                        </div>
                      </div>
                      <button 
                        onClick={() => handleLeaveAction(leave._id, 'rejected')}
                        disabled={actionLoading}
                        style={{ padding: '0.8rem 1rem', background: 'rgba(239, 68, 68, 0.1)', color: '#ef4444', border: '1px solid #ef4444', borderRadius: '8px', cursor: 'pointer', fontWeight: 'bold' }}
                      >
                        <FontAwesomeIcon icon={faCircleXmark} /> Reject
                      </button>
                    </div>
                  </div>
                ))
            )}
          </div>
        )}

        {/* BALANCE MODAL */}
        {showBalanceModal && (
            <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.8)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }}>
                <div style={{ background: '#1c1c1f', padding: '2rem', borderRadius: '16px', border: '1px solid rgba(255,255,255,0.1)', width: '400px' }}>
                    <h3>Manage Leave Balance</h3>
                    <p style={{ color: 'rgba(255,255,255,0.5)', fontSize: '0.9rem' }}>Updating balance for <b>{selectedStaff?.name}</b></p>
                    
                    <div style={{ margin: '1.5rem 0' }}>
                        <label style={{ display: 'block', fontSize: '0.85rem', color: 'rgba(255,255,255,0.4)', marginBottom: '0.5rem' }}>Current Balance (Days)</label>
                        <input 
                            type="number" 
                            value={newBalance}
                            onChange={(e) => setNewBalance(parseInt(e.target.value))}
                            style={{ width: '100%', padding: '0.8rem', background: '#0a0a0c', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '8px', color: 'white' }}
                        />
                    </div>

                    <div style={{ display: 'flex', gap: '1rem' }}>
                        <button 
                            onClick={updateBalance}
                            disabled={actionLoading}
                            style={{ flex: 1, padding: '0.8rem', background: '#22c55e', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                        >
                            Save Balance
                        </button>
                        <button 
                            onClick={() => setShowBalanceModal(false)}
                            style={{ flex: 1, padding: '0.8rem', background: 'rgba(255,255,255,0.05)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: 'pointer' }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </div>
        )}
      </div>
    </div>
  );
};

export default Attendance;
